/**
 * Interfaces for waybills, parcels, and related data structures
 */

import { Configuration } from './Configuration.interfaces';

/**
 * Interface representing a parcel based on Opr_Parcels table
 */
export interface Parcel {
  // Primary identifiers
  parcelId: number;
  parcelCode: string;
  parcelWaybillCode?: string;
  parcelPalletCode?: string;
  parcelShipmentCode?: string;
  parcelBrandCode: string;
  parcelDealerCode?: string;
  parcelBarcode?: string;
  parcelTrackingNumber?: string;
  
  // Dates
  parcelDate?: string;
  parcelDeliveryDate?: string;
  parcelEstimatedDeliveryDate?: string;
  parcelOriginalDeliveryDate?: string;
  parcelLastEventDate?: string;
  parcelTakeInChargeDate?: string;
  parcelLastUpdateAt?: string;
  parcelUpdatedAt?: string;
  parcelCreatedAt: string;
  
  // Delivery information
  parcelDeliveryStatus?: string;
  parcelDeliveryMethod?: string;
  parcelDeliveryType?: string;
  parcelDeliveryMaterial?: string;
  parcelDeliveryReason?: string;
  parcelTakeInChargeReason?: string;
  
  // Physical properties
  parcelDescription?: string;
  parcelWeight?: number;
  parcelVolume?: number;
  parcelLength?: number;
  parcelWidth?: number;
  parcelHeight?: number;
  parcelType?: string;
  
  // Location information
  parcelLocation?: string;
  parcelLatitude?: number;
  parcelLongitude?: number;
  parcelOriginalLatitude?: number;
  parcelOriginalLongitude?: number;
  
  // Special handling flags (Y/N strings from database)
  parcelFragile?: string;
  parcelHazardousMaterial?: string;
  parcelTemperatureSensitive?: string;
  parcelHighValueItem?: string;
  parcelRestrictedItem?: string;
  parcelCustomsClearanceRequired?: string;
  parcelDeliveryDateIsForced?: string;
  parcelDeliveryDateIsUpdated?: string;
  parcelDeliveryCoordinatesIsForced?: string;
  parcelDeliveryCoordinatesIsUpdated?: string;
  
  // Financial information
  parcelInsuredValue?: number;
  
  // Status and metadata
  parcelStatus?: string;
  parcelVersion?: number;
  parcelLastUpdateBy?: string;
  parcelUpdatedBy?: string;
  parcelLoadingList?: string;
}

/**
 * Possible statuses for a parcel
 */
export enum ParcelStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING = 'PENDING',
  IN_TRANSIT = 'IN_TRANSIT',
  DELIVERED = 'DELIVERED',
  RETURNED = 'RETURNED',
  CANCELLED = 'CANCELLED',
  DAMAGED = 'DAMAGED',
  LOST = 'LOST',
  WAITING = 'WAITING',
  PROBLEM = 'PROBLEM'
}

/**
 * Interface representing a waybill based on Opr_Waybills table
 */
export interface Waybill {
  // Primary identifiers
  waybillId: number;
  waybillCode: string;
  waybillPalletCode?: string;
  waybillShipmentCode: string;
  waybillTrackingNumber?: string;
  
  // Dates
  waybillDate?: string;
  waybillEstimatedDeliveryDate?: string;
  waybillActualDeliveryDate?: string;
  waybillOriginalDeliveryDate?: string;
  waybillCreatedAt: string;
  waybillUpdatedAt?: string;
  
  // Delivery information
  waybillDeliveryMethod?: string;
  waybillDeliveryType?: string;
  waybillTransitTimeHours?: number;
  
  // Location information
  waybillOriginLocation?: string;
  waybillDestinationLocation?: string;
  waybillLastKnownLocation?: string;
  waybillLatitude?: number;
  waybillLongitude?: number;
  waybillOriginalLatitude?: number;
  waybillOriginalLongitude?: number;
  
  // Cargo information
  waybillTotalWeight?: number;
  waybillTotalVolume?: number;
  waybillTotalParcels?: number;
  waybillFragile: boolean;
  waybillHazardousMaterial: boolean;
  waybillCustomsClearanceRequired: boolean;
  
  // Business information
  waybillBrandCode?: string;
  waybillDealerCode?: string;
  waybillCarrierCode?: string;
  
  // Status and metadata
  waybillStatus: string;
  waybillUpdatedBy?: string;
  waybillNote?: string;
  waybillFile?: string;
  waybillVersion: number;
  waybillLoadingList?: string;
}

/**
 * Possible statuses for a waybill based on database constraints
 */
export enum WaybillStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING = 'PENDING',
  IN_TRANSIT = 'IN_TRANSIT',
  DELIVERED = 'DELIVERED',
  RETURNED = 'RETURNED',
  CANCELLED = 'CANCELLED',
  PROBLEM = 'PROBLEM'
}

/**
 * Possible delivery methods for a waybill
 */
export enum WaybillDeliveryMethod {
  STANDARD = 'STANDARD',
  EXPRESS = 'EXPRESS',
  OVERNIGHT = 'OVERNIGHT',
  SAME_DAY = 'SAME_DAY',
  PICKUP = 'PICKUP'
}

/**
 * Possible delivery types for a waybill
 */
export enum WaybillDeliveryType {
  DOOR_TO_DOOR = 'DOOR_TO_DOOR',
  DOOR_TO_PORT = 'DOOR_TO_PORT',
  PORT_TO_DOOR = 'PORT_TO_DOOR',
  PORT_TO_PORT = 'PORT_TO_PORT'
}

/**
 * Interface for geographical coordinates
 */
export interface GeoCoordinates {
  latitude: number;
  longitude: number;
}

/**
 * Interface representing location information for a waybill
 */
export interface WaybillLocation {
  current?: GeoCoordinates;
  original?: GeoCoordinates;
  originLocation?: string;
  destinationLocation?: string;
  lastKnownLocation?: string;
}

/**
 * Interface for waybill delivery information
 */
export interface WaybillDeliveryInfo {
  method?: WaybillDeliveryMethod;
  type?: WaybillDeliveryType;
  transitTimeHours?: number;
  estimatedDeliveryDate?: string;
  actualDeliveryDate?: string;
  originalDeliveryDate?: string;
}

/**
 * Interface for waybill cargo information
 */
export interface WaybillCargoInfo {
  totalWeight?: number;
  totalVolume?: number;
  fragile: boolean;
  hazardousMaterial: boolean;
  customsClearanceRequired: boolean;
}

/**
 * Interface for waybill business information
 */
export interface WaybillBusinessInfo {
  brandCode?: string;
  dealerCode?: string;
  carrierCode?: string;
}

/**
 * Interface for waybill actions
 */
export interface WaybillActions {
  canPrint: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canAddParcel: boolean;
}

/**
 * Extended waybill with additional details
 */
export interface WaybillDetail extends Waybill {
  actions: WaybillActions;
  history?: WaybillHistoryEntry[];
}

/**
 * Interface for waybill history entries
 */
export interface WaybillHistoryEntry {
  id: string;
  timestamp: string;
  action: string;
  user: string;
  location: string;
  note?: string;
}

/**
 * Interface for creating a new waybill (omits auto-generated fields)
 */
export interface CreateWaybillRequest {
  waybillCode: string;
  waybillPalletCode?: string;
  waybillShipmentCode: string;
  waybillDate?: string;
  waybillEstimatedDeliveryDate?: string;
  waybillDeliveryMethod?: string;
  waybillDeliveryType?: string;
  waybillTransitTimeHours?: number;
  waybillTotalWeight?: number;
  waybillTotalVolume?: number;
  waybillBrandCode?: string;
  waybillDealerCode?: string;
  waybillCarrierCode?: string;
  waybillOriginLocation?: string;
  waybillDestinationLocation?: string;
  waybillTrackingNumber?: string;
  waybillFragile?: boolean;
  waybillHazardousMaterial?: boolean;
  waybillCustomsClearanceRequired?: boolean;
  waybillNote?: string;
  waybillLatitude?: number;
  waybillLongitude?: number;
  waybillOriginalDeliveryDate?: string;
  waybillOriginalLatitude?: number;
  waybillOriginalLongitude?: number;
}

/**
 * Interface for updating an existing waybill
 */
export interface UpdateWaybillRequest extends Partial<CreateWaybillRequest> {
  waybillId: number;
  waybillActualDeliveryDate?: string;
  waybillLastKnownLocation?: string;
  waybillStatus?: string;
  waybillUpdatedBy?: string;
  waybillFile?: string;
}

/**
 * Interface for dealer address information
 */
export interface DealerAddress {
  street?: string;
  number?: string;
  zip?: string;
  city?: string;
  province?: string;
  country?: string;
}

/**
 * Interface representing a dealer based on Opr_Dealers table
 */
export interface Dealer {
  // Primary identifiers
  dealerId: number;
  dealerCode: string;
  dealerName?: string;
  dealerLegalName?: string;
  dealerLegalCode?: string;
  
  // Tax and billing information
  dealerVatNumber?: string;
  dealerInternalBillingId?: string;
  dealerInternalId?: string;
  dealerSdiCode?: string;
  
  // Zone information
  dealerDeliveryZoneCode?: string;
  dealerBillingZoneCode?: string;
  
  // Legal address
  dealerLegalAddressStreet?: string;
  dealerLegalAddressNumber?: string;
  dealerLegalAddressZip?: string;
  dealerLegalAddressCity?: string;
  dealerLegalAddressProvince?: string;
  dealerLegalAddressCountry?: string;
  
  // Operating address
  dealerOperatingAddressStreet?: string;
  dealerOperatingAddressNumber?: string;
  dealerOperatingAddressZip?: string;
  dealerOperatingAddressCity?: string;
  dealerOperatingAddressProvince?: string;
  dealerOperatingAddressCountry?: string;
  
  // Business information
  dealerIncorporationDate?: string;
  dealerIndustry?: string;
  dealerStatus: string;
  dealerNotes?: string;
  
  // Geographic coordinates
  dealerLatitude?: number;
  dealerLongitude?: number;
  dealerCoordinatesOrigin?: string;
  
  // Metadata
  dealerVersion: number;
  dealerCreatedAt: string;

  // Backend-provided related configuration
  oprConfiguration?: Configuration;
}

/**
 * Possible statuses for a dealer
 */
export enum DealerStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED',
  PENDING = 'PENDING',
  TERMINATED = 'TERMINATED'
}

/**
 * Helper interface for structured dealer addresses
 */
export interface DealerAddresses {
  legal: DealerAddress;
  operating: DealerAddress;
}

/**
 * Interface for creating a new dealer (omits auto-generated fields)
 */
export interface CreateDealerRequest {
  dealerCode: string;
  dealerName?: string;
  dealerLegalName?: string;
  dealerLegalCode?: string;
  dealerVatNumber?: string;
  dealerInternalBillingId?: string;
  dealerInternalId?: string;
  dealerSdiCode?: string;
  dealerDeliveryZoneCode?: string;
  dealerBillingZoneCode?: string;
  
  // Legal address
  dealerLegalAddressStreet?: string;
  dealerLegalAddressNumber?: string;
  dealerLegalAddressZip?: string;
  dealerLegalAddressCity?: string;
  dealerLegalAddressProvince?: string;
  dealerLegalAddressCountry?: string;
  
  // Operating address
  dealerOperatingAddressStreet?: string;
  dealerOperatingAddressNumber?: string;
  dealerOperatingAddressZip?: string;
  dealerOperatingAddressCity?: string;
  dealerOperatingAddressProvince?: string;
  dealerOperatingAddressCountry?: string;
  
  dealerIncorporationDate?: string;
  dealerIndustry?: string;
  dealerStatus?: string;
  dealerNotes?: string;
  dealerLatitude?: number;
  dealerLongitude?: number;
  dealerCoordinatesOrigin?: string;
}

/**
 * Interface for updating an existing dealer
 */
export interface UpdateDealerRequest extends Partial<CreateDealerRequest> {
  dealerId: number;
}

/**
 * Extended dealer interface with computed properties
 */
export interface DealerDetail extends Dealer {
  // Computed address objects
  legalAddress: DealerAddress;
  operatingAddress: DealerAddress;
  
  // Helper properties
  hasLegalAddress: boolean;
  hasOperatingAddress: boolean;
  useSameAddress: boolean;
  
  // Geographic information
  hasCoordinates: boolean;
  formattedCoordinates?: string;
  
  // Status helpers
  isActive: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

/**
 * Enum for brand status
 */
export enum BrandStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED'
}

/**
 * Interface representing a brand based on Opr_Brands table
 */
export interface Brand {
  brandId: number;
  brandCode: string;
  brandAffiliationCode?: string;
  brandAffiliationType?: string;
  brandAcronym?: string;
  brandName?: string;
  brandStatus: BrandStatus;
  brandDescription?: string;
  brandCreatedAt?: string;
}
